import Auth from '../api/auth';
import { clearCookiesOnLogout } from '../store/utils/api';

const parseErrorCode = error => Promise.reject(error);

// A 401 from these is an expected outcome (bad credentials, signing out a
// session the server already dropped), not a sign that our session died.
// Forcing a logout on them would redirect out from under those flows, and
// validate_token is what we probe with — so it must never re-enter the probe.
const AUTH_EXEMPT_PATHS = [
  '/auth/sign_in',
  '/auth/sign_out',
  '/auth/password',
  '/auth/validate_token',
];

const isAuthExempt = url =>
  AUTH_EXEMPT_PATHS.some(path => String(url || '').includes(path));

export default axios => {
  const { apiHost = '' } = window.chatwootConfig || {};
  const wootApi = axios.create({ baseURL: `${apiHost}/` });

  // Read the auth cookie on every request rather than once at page load.
  // Previously these headers were frozen into defaults.headers.common when the
  // instance was created, so a tab that started without a cookie — or whose
  // cookie went stale while it stayed open — kept sending a dead token for its
  // entire lifetime. Every call 401'd, leaving an empty sidebar and an inbox
  // list stuck on "loading" with no way to recover short of a hard reload.
  wootApi.interceptors.request.use(config => {
    const authData = Auth.getAuthData();
    if (authData) {
      const {
        'access-token': accessToken,
        'token-type': tokenType,
        client,
        expiry,
        uid,
      } = authData;
      Object.assign(config.headers, {
        'access-token': accessToken,
        'token-type': tokenType,
        client,
        expiry,
        uid,
      });
    }
    return config;
  });

  // Confirm the session is genuinely gone before tearing anything down, so a
  // one-off 401 can never sign out a healthy user. Shared across concurrent
  // failures: the dashboard fires many calls at once and they would otherwise
  // each launch their own probe.
  let sessionProbe = null;
  let isLoggingOut = false;

  const sessionIsExpired = () => {
    if (!sessionProbe) {
      sessionProbe = Auth.validityCheck()
        .then(() => false)
        .catch(error => error?.response?.status === 401)
        .finally(() => {
          sessionProbe = null;
        });
    }
    return sessionProbe;
  };

  // Response parsing interceptor
  wootApi.interceptors.response.use(
    response => response,
    error => {
      const status = error?.response?.status;
      if (status === 401 && !isLoggingOut && !isAuthExempt(error?.config?.url)) {
        sessionIsExpired().then(expired => {
          if (expired && !isLoggingOut) {
            isLoggingOut = true;
            clearCookiesOnLogout();
          }
        });
      }
      return parseErrorCode(error);
    }
  );
  return wootApi;
};
